import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { ProjectCard } from '@captaincause/ui/ProjectCard'
import { Button } from 'components/Button'
import { GiveProjectsGrid } from 'components/GiveProjectsGrid'
import { Panels } from './Panels'
import type { Dift, Project, ProjectId } from '@captaincause/types'

interface DiftProjectListProps {
  dift?: Dift
  clearFilters?: () => void
  projects: Project[]
}

export function DiftProjectList({
  dift = undefined,
  clearFilters = undefined,
  projects,
}: DiftProjectListProps) {
  const { t } = useTranslation('projects.page')

  const { track } = useMixpanel()

  const locale = useNextLocale()

  const [projectId, setProjectId] = useState<ProjectId | undefined>()

  if (!dift) return null

  const close = () => {
    setProjectId(undefined)
  }

  const donate = (id: ProjectId) => {
    if (id) {
      setProjectId(id)
      track('Click Choose Project', {
        project_id: id,
        dift_code: dift.code,
        dift_id: dift.id,
        dift_with_message: !!dift.message,
      })
    }
  }

  const clear = () => {
    clearFilters?.()
  }

  const projectIdList = projects.map(({ id }) => id)

  const switchProject = (direction: 'left' | 'right') => {
    function getId(prev: string | undefined) {
      const currentIndex = projectIdList.indexOf(prev || '')
      const nextIndex = currentIndex + (direction === 'left' ? -1 : 1)
      if (nextIndex < 0) {
        return projectIdList[projectIdList.length - 1]
      }
      if (nextIndex >= projectIdList.length) {
        return projectIdList[0]
      }

      return projectIdList[nextIndex]
    }

    setProjectId(getId)
  }

  return (
    <>
      <GiveProjectsGrid
        size={projects.length}
        className="px-4 py-10 md:px-8 lg:px-10 xl:px-20"
      >
        {projects.length > 0 ? (
          projects.map((projectItem, index) => (
            <ProjectCard
              key={projectItem.name}
              project={projectItem}
              donate={donate}
              amount={dift.amount}
              progressBar={false}
              compact={false}
              hasPriorityImage={index < 6}
              locale={locale}
              variant="give"
            />
          ))
        ) : (
          <div className="my-12 flex flex-col items-center md:m-auto">
            <div className="text-center text-lg">
              {t('search.not-found.title')}
            </div>
            <div className="text-base">{t('search.not-found.retry')}</div>
            <Button variant="secondary" small onClick={clear} className="mt-4">
              {t('search.not-found.reset')}
            </Button>
          </div>
        )}
      </GiveProjectsGrid>

      <Panels
        switchProject={switchProject}
        close={close}
        projectId={projectId}
        dift={dift}
      />
    </>
  )
}
